function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/utils';
import { useGridApiEventHandler } from "../../utils/useGridApiEventHandler.js";
import { useGridApiMethod } from "../../utils/useGridApiMethod.js";
import { useGridLogger } from "../../utils/useGridLogger.js";
import { gridColumnLookupSelector } from "../columns/gridColumnsSelector.js";
import { gridSortedRowEntriesSelector, gridSortedRowIdsSelector, gridSortModelSelector } from "./gridSortingSelector.js";
import { GRID_ROOT_GROUP_ID, gridRowTreeSelector } from "../rows/index.js";
import { useFirstRender } from "../../utils/useFirstRender.js";
import { useGridRegisterStrategyProcessor, GRID_DEFAULT_STRATEGY } from "../../core/strategyProcessing/index.js";
import { buildAggregatedSortingApplier, mergeStateWithSortModel, getNextGridSortDirection, sanitizeSortModel } from "./gridSortingUtils.js";
import { useGridRegisterPipeProcessor } from "../../core/pipeProcessing/index.js";
import { getTreeNodeDescendants } from "../rows/gridRowsUtils.js";
export var sortingStateInitializer = function(state, props) {
    var _props_initialState_sorting, _props_initialState;
    var _props_sortModel, _ref;
    var sortModel = (_ref = (_props_sortModel = props.sortModel) !== null && _props_sortModel !== void 0 ? _props_sortModel : (_props_initialState = props.initialState) === null || _props_initialState === void 0 ? void 0 : (_props_initialState_sorting = _props_initialState.sorting) === null || _props_initialState_sorting === void 0 ? void 0 : _props_initialState_sorting.sortModel) !== null && _ref !== void 0 ? _ref : [];
    return _extends({}, state, {
        sorting: {
            sortModel: sanitizeSortModel(sortModel, props.disableMultipleColumnsSorting),
            sortedRows: []
        }
    });
};
/**
 * @requires useGridRows (event)
 * @requires useGridColumns (event)
 */ export var useGridSorting = function(apiRef, props) {
    var _props_initialState_sorting, _props_initialState;
    var logger = useGridLogger(apiRef, 'useGridSorting');
    apiRef.current.registerControlState({
        stateId: 'sortModel',
        propModel: props.sortModel,
        propOnChange: props.onSortModelChange,
        stateSelector: gridSortModelSelector,
        changeEvent: 'sortModelChange'
    });
    var upsertSortModel = React.useCallback(function(field, sortItem) {
        var sortModel = gridSortModelSelector(apiRef);
        var existingIdx = sortModel.findIndex(function(c) {
            return c.field === field;
        });
        var newSortModel = _to_consumable_array(sortModel);
        if (existingIdx > -1) {
            if ((sortItem === null || sortItem === void 0 ? void 0 : sortItem.sort) == null) {
                newSortModel.splice(existingIdx, 1);
            } else {
                newSortModel.splice(existingIdx, 1, sortItem);
            }
        } else {
            newSortModel = _to_consumable_array(sortModel).concat([
                sortItem
            ]);
        }
        return newSortModel;
    }, [
        apiRef
    ]);
    var createSortItem = React.useCallback(function(col, directionOverride) {
        var sortModel = gridSortModelSelector(apiRef);
        var existing = sortModel.find(function(c) {
            return c.field === col.field;
        });
        if (existing) {
            var _col_sortingOrder;
            var nextSort = directionOverride === undefined ? getNextGridSortDirection((_col_sortingOrder = col.sortingOrder) !== null && _col_sortingOrder !== void 0 ? _col_sortingOrder : props.sortingOrder, existing.sort) : directionOverride;
            return nextSort === undefined ? undefined : _extends({}, existing, {
                sort: nextSort
            });
        }
        var _col_sortingOrder1;
        return {
            field: col.field,
            sort: directionOverride === undefined ? getNextGridSortDirection((_col_sortingOrder1 = col.sortingOrder) !== null && _col_sortingOrder1 !== void 0 ? _col_sortingOrder1 : props.sortingOrder) : directionOverride
        };
    }, [
        apiRef,
        props.sortingOrder
    ]);
    var addColumnMenuItem = React.useCallback(function(columnMenuItems, colDef) {
        if (colDef == null || colDef.sortable === false || props.disableColumnSorting) {
            return columnMenuItems;
        }
        var sortingOrder = colDef.sortingOrder || props.sortingOrder;
        if (sortingOrder.some(function(item) {
            return !!item;
        })) {
            return _to_consumable_array(columnMenuItems).concat([
                'columnMenuSortItem'
            ]);
        }
        return columnMenuItems;
    }, [
        props.sortingOrder,
        props.disableColumnSorting
    ]);
    /**
   * API METHODS
   */ var applySorting = React.useCallback(function() {
        apiRef.current.setState(function(state) {
            if (props.sortingMode === 'server') {
                logger.debug('Skipping sorting rows as sortingMode = server');
                return _extends({}, state, {
                    sorting: _extends({}, state.sorting, {
                        sortedRows: getTreeNodeDescendants(gridRowTreeSelector(apiRef), GRID_ROOT_GROUP_ID, false)
                    })
                });
            }
            var sortModel = gridSortModelSelector(state, apiRef.current.instanceId);
            var sortRowList = buildAggregatedSortingApplier(sortModel, apiRef);
            var sortedRows = apiRef.current.applyStrategyProcessor('sorting', {
                sortRowList: sortRowList
            });
            return _extends({}, state, {
                sorting: _extends({}, state.sorting, {
                    sortedRows: sortedRows
                })
            });
        });
        apiRef.current.publishEvent('sortedRowsSet');
        apiRef.current.forceUpdate();
    }, [
        apiRef,
        logger,
        props.sortingMode
    ]);
    var setSortModel = React.useCallback(function(model) {
        var currentModel = gridSortModelSelector(apiRef);
        if (currentModel !== model) {
            logger.debug("Setting sort model");
            apiRef.current.setState(mergeStateWithSortModel(model, props.disableMultipleColumnsSorting));
            apiRef.current.forceUpdate();
            apiRef.current.applySorting();
        }
    }, [
        apiRef,
        logger,
        props.disableMultipleColumnsSorting
    ]);
    var sortColumn = React.useCallback(function(field, direction, allowMultipleSorting) {
        var column = apiRef.current.getColumn(field);
        var sortItem = createSortItem(column, direction);
        var sortModel;
        if (!allowMultipleSorting || props.disableMultipleColumnsSorting) {
            sortModel = (sortItem === null || sortItem === void 0 ? void 0 : sortItem.sort) == null ? [] : [
                sortItem
            ];
        } else {
            sortModel = upsertSortModel(column.field, sortItem);
        }
        apiRef.current.setSortModel(sortModel);
    }, [
        apiRef,
        upsertSortModel,
        createSortItem,
        props.disableMultipleColumnsSorting
    ]);
    var getSortModel = React.useCallback(function() {
        return gridSortModelSelector(apiRef);
    }, [
        apiRef
    ]);
    var getSortedRows = React.useCallback(function() {
        var sortedRows = gridSortedRowEntriesSelector(apiRef);
        return sortedRows.map(function(row) {
            return row.model;
        });
    }, [
        apiRef
    ]);
    var getSortedRowIds = React.useCallback(function() {
        return gridSortedRowIdsSelector(apiRef);
    }, [
        apiRef
    ]);
    var getRowIdFromRowIndex = React.useCallback(function(index) {
        return apiRef.current.getSortedRowIds()[index];
    }, [
        apiRef
    ]);
    var sortApi = {
        getSortModel: getSortModel,
        getSortedRows: getSortedRows,
        getSortedRowIds: getSortedRowIds,
        getRowIdFromRowIndex: getRowIdFromRowIndex,
        setSortModel: setSortModel,
        sortColumn: sortColumn,
        applySorting: applySorting
    };
    useGridApiMethod(apiRef, sortApi, 'public');
    /**
   * PRE-PROCESSING
   */ var stateExportPreProcessing = React.useCallback(function(prevState, context) {
        var // Always export if the model has been initialized
        _props_initialState_sorting, _props_initialState;
        var sortModelToExport = gridSortModelSelector(apiRef);
        var shouldExportSortModel = // Always export if the `exportOnlyDirtyModels` property is not activated
        !context.exportOnlyDirtyModels || // Always export if the model is controlled
        props.sortModel != null || ((_props_initialState = props.initialState) === null || _props_initialState === void 0 ? void 0 : (_props_initialState_sorting = _props_initialState.sorting) === null || _props_initialState_sorting === void 0 ? void 0 : _props_initialState_sorting.sortModel) != null || // Export if the model is not empty
        sortModelToExport.length > 0;
        if (!shouldExportSortModel) {
            return prevState;
        }
        return _extends({}, prevState, {
            sorting: {
                sortModel: sortModelToExport
            }
        });
    }, [
        apiRef,
        props.sortModel,
        (_props_initialState = props.initialState) === null || _props_initialState === void 0 ? void 0 : (_props_initialState_sorting = _props_initialState.sorting) === null || _props_initialState_sorting === void 0 ? void 0 : _props_initialState_sorting.sortModel
    ]);
    var stateRestorePreProcessing = React.useCallback(function(params, context) {
        var _context_stateToRestore_sorting;
        var sortModel = (_context_stateToRestore_sorting = context.stateToRestore.sorting) === null || _context_stateToRestore_sorting === void 0 ? void 0 : _context_stateToRestore_sorting.sortModel;
        if (sortModel == null) {
            return params;
        }
        apiRef.current.setState(mergeStateWithSortModel(sortModel, props.disableMultipleColumnsSorting));
        return _extends({}, params, {
            callbacks: _to_consumable_array(params.callbacks).concat([
                apiRef.current.applySorting
            ])
        });
    }, [
        apiRef,
        props.disableMultipleColumnsSorting
    ]);
    var flatSortingMethod = React.useCallback(function(params) {
        var rowTree = gridRowTreeSelector(apiRef);
        var rootGroupNode = rowTree[GRID_ROOT_GROUP_ID];
        var sortedChildren = params.sortRowList ? params.sortRowList(rootGroupNode.children.map(function(childId) {
            return rowTree[childId];
        })) : _to_consumable_array(rootGroupNode.children);
        if (rootGroupNode.footerId != null) {
            sortedChildren.push(rootGroupNode.footerId);
        }
        return sortedChildren;
    }, [
        apiRef
    ]);
    useGridRegisterPipeProcessor(apiRef, 'exportState', stateExportPreProcessing);
    useGridRegisterPipeProcessor(apiRef, 'restoreState', stateRestorePreProcessing);
    useGridRegisterStrategyProcessor(apiRef, GRID_DEFAULT_STRATEGY, 'sorting', flatSortingMethod);
    /**
   * EVENTS
   */ var handleColumnHeaderClick = React.useCallback(function(param, event) {
        var field = param.field, colDef = param.colDef;
        if (!colDef.sortable || props.disableColumnSorting) {
            return;
        }
        var allowMultipleSorting = event.shiftKey || event.metaKey || event.ctrlKey;
        sortColumn(field, undefined, allowMultipleSorting);
    }, [
        sortColumn,
        props.disableColumnSorting
    ]);
    var handleColumnHeaderKeyDown = React.useCallback(function(param, event) {
        var field = param.field, colDef = param.colDef;
        if (!colDef.sortable || props.disableColumnSorting) {
            return;
        }
        // Ctrl + Enter opens the column menu
        if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
            sortColumn(field, undefined, event.shiftKey);
        }
    }, [
        sortColumn,
        props.disableColumnSorting
    ]);
    var handleColumnsChange = React.useCallback(function() {
        // When the columns change we check that the sorted columns are still part of the dataset
        var sortModel = gridSortModelSelector(apiRef);
        var latestColumns = gridColumnLookupSelector(apiRef);
        if (sortModel.length > 0) {
            var newModel = sortModel.filter(function(sortItem) {
                return latestColumns[sortItem.field];
            });
            if (newModel.length < sortModel.length) {
                apiRef.current.setSortModel(newModel);
            }
        }
    }, [
        apiRef
    ]);
    var handleStrategyProcessorChange = React.useCallback(function(methodName) {
        if (methodName === 'sorting') {
            apiRef.current.applySorting();
        }
    }, [
        apiRef
    ]);
    useGridRegisterPipeProcessor(apiRef, 'columnMenu', addColumnMenuItem);
    useGridApiEventHandler(apiRef, 'columnHeaderClick', handleColumnHeaderClick);
    useGridApiEventHandler(apiRef, 'columnHeaderKeyDown', handleColumnHeaderKeyDown);
    useGridApiEventHandler(apiRef, 'rowsSet', apiRef.current.applySorting);
    useGridApiEventHandler(apiRef, 'columnsChange', handleColumnsChange);
    useGridApiEventHandler(apiRef, 'activeStrategyProcessorChange', handleStrategyProcessorChange);
    /**
   * 1ST RENDER
   */ useFirstRender(function() {
        apiRef.current.applySorting();
    });
    /**
   * EFFECTS
   */ useEnhancedEffect(function() {
        if (props.sortModel !== undefined) {
            apiRef.current.setSortModel(props.sortModel);
        }
    }, [
        apiRef,
        props.sortModel
    ]);
};
